import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  Presentation, ChevronDown, MoreVertical, Pencil, ClipboardX, Archive, RotateCcw,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { cn } from '@repo/ui/src/lib/utils';
import type { ClassGroupListRow, ClassSlotRow } from '@repo/shared-types';
import { useClasses, useArchiveClass, useUnarchiveClass } from '../../hooks/useClasses';
import { useSubjects } from '../../hooks/useSubjects';
import { useShowTeacher, useTeacherOptions } from '../../hooks/useTeachers';
import { useMembers } from '../../hooks/useTeam';
import { useEffectiveBranchId } from '../../hooks/useBranchScope';
import { useCan } from '../../hooks/useCan';
import { resolveTeacher, type TeacherLike } from '../../lib/teacherDisplay';
import { classColorStyle } from '../../lib/classColors';
import { groupSlotsByTime } from '../../lib/classSchedule';
import { capacityDisplay } from '../../lib/classCapacity';
import { fmtHouseDate } from '../../lib/appliedDate';
import { ListSkeleton } from '../../components/ListSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { CardList, CardRow } from '../../components/CardList';
import { ClassFormSheet } from './ClassFormSheet';
import { EndClassDialog } from './EndClassDialog';

const ALL_TEACHERS = '__all__';
const ALL_SUBJECTS = '__all__';

// i18next's TFunction isn't structurally assignable to a plain callback (see
// StudentsPage's own KhoaBadgeT note) — a plain signature instead.
type ListT = (key: string, options?: Record<string, unknown>) => string;

/** Status line under "Bắt đầu" (README decision 3 — no separate status
 *  column, so a jump between phases never shifts the other columns). */
function statusLine(row: ClassGroupListRow, t: ListT): string | null {
  if (row.phase === 'UPCOMING') return t('classes.list.statusUpcoming');
  if (row.phase === 'ENDED') {
    return t('classes.list.statusEnded', {
      from: fmtHouseDate(row.startsOn),
      to: row.endsOn ? fmtHouseDate(row.endsOn) : '',
    });
  }
  return null;
}

function ColorSquare({ color }: { color: ClassGroupListRow['color'] }) {
  const style = classColorStyle(color);
  if (!style) {
    return <span className="h-3 w-3 shrink-0 rounded-[3px] border border-dashed border-surface-300" aria-hidden />;
  }
  return <span className="h-3 w-3 shrink-0 rounded-[3px] border opacity-90" style={style} aria-hidden />;
}

function RowSchedule({ slots, t }: { slots: ClassSlotRow[]; t: ListT }) {
  const groups = groupSlotsByTime(slots);
  return (
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
      {groups.map((g) => (
        <span key={g.time} className="inline-flex items-center gap-0.5">
          {g.days.map((d) => (
            <span key={d} className="inline-flex h-[20.8px] items-center rounded-md bg-surface-100 px-1.5 text-[11.5px] font-semibold text-surface-600">
              {t(`days.${d}`)}
            </span>
          ))}
          <span className="tabular ml-1 text-[13px] text-surface-800">{g.time}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Fix wave (screenshot gate S2/1 + S2/2) — the per-row ⋮ menu the handoff
 * frame draws (Sửa lớp / Kết thúc lớp / Lưu trữ, Khôi phục for an ARCHIVED
 * row), gated on `useCan('manage:slots')` like every other write affordance
 * on this page. A plain sibling of the row's own `<Link>` (never nested
 * inside it — two interactive elements of the same kind, one inside the
 * other, is invalid HTML and the touch/click targeting gets confused). */
function RowMenu({
  row, t, onEdit, onEnd, onArchive, onUnarchive,
}: {
  row: ClassGroupListRow;
  t: ListT;
  onEdit: (row: ClassGroupListRow) => void;
  onEnd: (row: ClassGroupListRow) => void;
  onArchive: (row: ClassGroupListRow) => void;
  onUnarchive: (row: ClassGroupListRow) => void;
}) {
  const canEnd = row.status === 'ACTIVE' && row.phase !== 'ENDED';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('classDetail.roster.rowMenuAria', { name: row.displayName })}
          // Re-review fix (new defect 4, §6.1): 44px tap target below md —
          // this same trigger renders inside the phone CardList's `actions`
          // slot (a touch surface) and inside the desktop table row (a
          // mouse one), so only the phone size needs to grow; the icon
          // itself stays the same visual size, just more padding around it.
          className="flex h-[44px] w-[44px] items-center justify-center rounded-lg text-surface-500 hover:bg-surface-100 md:h-[28.8px] md:w-[28.8px]"
        >
          <MoreVertical className="h-[14.4px] w-[14.4px]" strokeWidth={1.8} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onEdit(row)} className="cursor-pointer">
          <Pencil className="mr-2 h-3.5 w-3.5" /> {t('classDetail.header.editClass')}
        </DropdownMenuItem>
        {canEnd && (
          <DropdownMenuItem onSelect={() => onEnd(row)} className="cursor-pointer">
            <ClipboardX className="mr-2 h-3.5 w-3.5" /> {t('classDetail.header.endClass')}
          </DropdownMenuItem>
        )}
        {row.status === 'ARCHIVED' ? (
          <DropdownMenuItem onSelect={() => onUnarchive(row)} className="cursor-pointer">
            <RotateCcw className="mr-2 h-3.5 w-3.5" /> {t('classDetail.header.unarchive')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={() => onArchive(row)}
            className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <Archive className="mr-2 h-3.5 w-3.5" /> {t('classDetail.header.archive')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RowTeachers({ teacherIds, members }: { teacherIds: string[]; members: TeacherLike[] | undefined }) {
  const resolved = teacherIds.map((id) => resolveTeacher(members ?? [], id)).filter((x): x is NonNullable<typeof x> => x !== null);
  if (resolved.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px] text-surface-600">
      {resolved.map((t, i) => (
        <span key={i} className="inline-flex min-w-0 items-center gap-1.2 whitespace-nowrap">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: t.color }} />
          <span className="truncate">{t.name}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * "Danh sách" tab (S2/1–4): table ≥768px, one card per row below it. Row
 * click opens the class detail page — the whole row is a `<Link>` (matching
 * the mock's own `<a>`-wrapped row), so middle-click/ctrl-click still opens a
 * new tab.
 */
export function ClassListTab({ showEnded, showArchived }: { showEnded: boolean; showArchived: boolean }) {
  const { t } = useTranslation('scheduling');
  const showTeacher = useShowTeacher();
  const teacherOptions = useTeacherOptions();
  const { data: subjects } = useSubjects();
  // includeRemoved: an ENDED/ARCHIVED row may point at a since-removed
  // teacher — same reasoning as TimetableGridTab's own `members` query.
  const { data: members } = useMembers({ includeRemoved: true });
  const branchId = useEffectiveBranchId();
  const canManage = useCan('manage:slots');
  const archiveClass = useArchiveClass();
  const unarchiveClass = useUnarchiveClass();
  const [teacherFilter, setTeacherFilter] = useState<string | undefined>(undefined);
  const [subjectFilter, setSubjectFilter] = useState<string | undefined>(undefined);
  // Fix wave (S2/1 + S2/2) — the row menu's own targets, one dialog/sheet
  // instance shared by every row (keyed to whichever row opened it).
  const [editTarget, setEditTarget] = useState<ClassGroupListRow | null>(null);
  const [endTarget, setEndTarget] = useState<ClassGroupListRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ClassGroupListRow | null>(null);

  const baseFilters = {
    teacherId: showTeacher ? teacherFilter : undefined,
    subjectId: subjectFilter,
    branchId,
  };
  const { data: activeRows, isLoading, isError } = useClasses({ ...baseFilters, status: 'ACTIVE' });
  const { data: endedRows } = useClasses({ ...baseFilters, status: 'ENDED' }, { enabled: showEnded });
  const { data: archivedRows } = useClasses({ ...baseFilters, status: 'ARCHIVED' }, { enabled: showArchived });

  const rows = useMemo(() => {
    const all = [
      ...(activeRows ?? []),
      ...(showEnded ? endedRows ?? [] : []),
      ...(showArchived ? archivedRows ?? [] : []),
    ];
    return all.sort(
      (a, b) => a.subject.name.localeCompare(b.subject.name) || a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id),
    );
  }, [activeRows, endedRows, archivedRows, showEnded, showArchived]);

  const hasFilter = Boolean(teacherFilter || subjectFilter);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showTeacher ? (
          <Select value={teacherFilter ?? ALL_TEACHERS} onValueChange={(v) => setTeacherFilter(v === ALL_TEACHERS ? undefined : v)}>
            <SelectTrigger className="h-[28.8px] w-auto gap-2 text-[13px]"><SelectValue placeholder={t('classes.filters.allTeachers')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TEACHERS}>{t('classes.filters.allTeachers')}</SelectItem>
              {teacherOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
                    {opt.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Select value={subjectFilter ?? ALL_SUBJECTS} onValueChange={(v) => setSubjectFilter(v === ALL_SUBJECTS ? undefined : v)}>
          <SelectTrigger className="h-[28.8px] w-auto gap-2 text-[13px]"><SelectValue placeholder={t('classes.filters.allSubjects')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SUBJECTS}>{t('classes.filters.allSubjects')}</SelectItem>
            {(subjects ?? []).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-[10px] border border-surface-200 bg-card overflow-hidden">
        {isLoading ? (
          <ListSkeleton label={t('classes.list.loading')} />
        ) : isError ? (
          <p className="flex h-24 items-center justify-center text-sm text-muted-foreground">{t('classes.list.loadError')}</p>
        ) : rows.length === 0 ? (
          hasFilter ? (
            <EmptyState
              kind="filterMiss"
              title={t('classes.list.filterEmptyTitle')}
              onClear={() => { setTeacherFilter(undefined); setSubjectFilter(undefined); }}
            />
          ) : (
            <EmptyState kind="teach" icon={Presentation} title={t('classes.empty.title')} body={t('classes.empty.body')} />
          )
        ) : (
          <>
            {/* Desktop/tablet: a CSS-grid "table" ported from the S2 markup
                (the mock itself never uses a real <table> for this row) —
                each row is one <Link>, matching the mock's `<a>`-wrapped row
                and giving native middle-click/ctrl-click. */}
            <div className="hidden md:block">
              <div
                className="grid items-center h-[35.2px] px-4 border-b border-surface-200 text-[11px] font-semibold uppercase tracking-wide text-surface-500"
                style={{ gridTemplateColumns: `minmax(0, 2.2fr) 1fr 2.2fr 1.6fr 1fr 1.3fr ${canManage ? '32px ' : ''}40px` }}
              >
                <span>{t('classes.list.columnClass')}</span>
                <span>{t('classes.list.columnSubject')}</span>
                <span>{t('classes.list.columnSchedule')}</span>
                <span>{t('classes.list.columnTeacher')}</span>
                <span>{t('classes.list.columnCapacity')}</span>
                <span>{t('classes.list.columnStartsOn')}</span>
                {canManage && <span />}
                <span />
              </div>
              {rows.map((r) => {
                const capacity = capacityDisplay(r.rosterCount, r.maxCapacity, t);
                const status = statusLine(r, t);
                return (
                  <div
                    key={r.id}
                    className="grid items-center min-h-[52px] gap-y-1 px-4 py-2 border-b border-surface-100 text-sm text-surface-800 last:border-b-0 hover:bg-surface-50"
                    style={{ gridTemplateColumns: `minmax(0, 2.2fr) 1fr 2.2fr 1.6fr 1fr 1.3fr ${canManage ? '32px ' : ''}40px` }}
                  >
                    {/* `className="contents"` — the Link disappears from
                        layout so its children lay out as direct grid items
                        (columns 1-6), while the ⋮ menu below is a plain
                        SIBLING grid item, never nested inside this Link
                        (fix wave S2/1 + S2/2: the row used to be one single
                        `<Link>`, which cannot contain another interactive
                        element). */}
                    <Link to={`/classes/${r.id}`} className="contents">
                      <span className="flex min-w-0 items-center gap-2.5">
                        <ColorSquare color={r.color} />
                        <span className={cn('truncate', r.name ? 'font-semibold text-surface-900' : 'font-medium text-surface-500')}>
                          {r.displayName}
                        </span>
                      </span>
                      <span className="truncate text-surface-600">{r.subject.name}</span>
                      <RowSchedule slots={r.slots} t={t} />
                      <RowTeachers teacherIds={r.teacherIds} members={members} />
                      <span className="tabular">
                        {capacity.pill ? (
                          <span className="inline-flex h-[22.4px] items-center gap-1 rounded-full bg-surface-100 px-2 text-xs font-semibold text-surface-600">
                            {capacity.text}
                          </span>
                        ) : (
                          <span className="text-surface-900">{capacity.text}</span>
                        )}
                      </span>
                      <span className="flex flex-col text-[13.5px] text-surface-600">
                        <span className="tabular">{fmtHouseDate(r.startsOn)}</span>
                        {status ? (
                          <span className={cn('text-xs font-medium', r.phase === 'ENDED' ? 'text-surface-500' : 'text-brand-600')}>
                            {status}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                    {canManage && (
                      <RowMenu
                        row={r}
                        t={t}
                        onEdit={setEditTarget}
                        onEnd={setEndTarget}
                        onArchive={setArchiveTarget}
                        onUnarchive={(row) => unarchiveClass.mutate(row.id)}
                      />
                    )}
                    {/* The chevron stays as the row link — same destination
                        as the content Link above, hidden from the a11y tree
                        so it is not a second unlabeled stop for keyboard/
                        screen-reader users (the row's own Link already
                        names the destination). */}
                    <Link to={`/classes/${r.id}`} aria-hidden tabIndex={-1} className="flex justify-end text-surface-400">
                      <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                    </Link>
                  </div>
                );
              })}
            </div>

            <CardList>
              {rows.map((r) => {
                const capacity = capacityDisplay(r.rosterCount, r.maxCapacity, t);
                const status = statusLine(r, t);
                return (
                  <CardRow
                    key={r.id}
                    to={`/classes/${r.id}`}
                    title={
                      <span className="flex min-w-0 items-center gap-2">
                        <ColorSquare color={r.color} />
                        <span className="truncate">{r.displayName}</span>
                      </span>
                    }
                    titleRight={
                      capacity.pill ? (
                        <span className="tabular inline-flex h-[22.4px] items-center rounded-full bg-surface-100 px-2 text-xs font-semibold text-surface-600">
                          {capacity.text}
                        </span>
                      ) : (
                        <span className="tabular text-[13.5px] text-surface-600">{capacity.text}</span>
                      )
                    }
                    line2={
                      <span className="flex flex-wrap items-center gap-1">
                        <RowSchedule slots={r.slots} t={t} />
                        <span>· {r.subject.name}</span>
                        {status ? <span className="font-medium">· {status}</span> : null}
                      </span>
                    }
                    actions={canManage ? (
                      <RowMenu
                        row={r}
                        t={t}
                        onEdit={setEditTarget}
                        onEnd={setEndTarget}
                        onArchive={setArchiveTarget}
                        onUnarchive={(row) => unarchiveClass.mutate(row.id)}
                      />
                    ) : undefined}
                  />
                );
              })}
            </CardList>
          </>
        )}
      </div>

      <ClassFormSheet
        open={editTarget !== null}
        onOpenChange={(o) => { if (!o) setEditTarget(null); }}
        classId={editTarget?.id}
      />
      <EndClassDialog
        open={endTarget !== null}
        onOpenChange={(o) => { if (!o) setEndTarget(null); }}
        classId={endTarget?.id ?? ''}
      />
      <AlertDialog open={archiveTarget !== null} onOpenChange={(o) => { if (!o) setArchiveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('classDetail.archiveConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('classDetail.archiveConfirm.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('slot.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!archiveTarget) return;
                archiveClass.mutate(archiveTarget.id, { onSuccess: () => setArchiveTarget(null) });
              }}
              disabled={archiveClass.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('classDetail.archiveConfirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
